import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import { couponService } from "../../services/couponService";
import { parseApiError } from "../../utils/errorHandler";

interface Coupon {
  _id: string;
  code: string;
  usedCount: number;
}

interface DeleteCouponDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  coupon: Coupon | null;
}

export function DeleteCouponDialog({
  isOpen,
  onClose,
  onSuccess,
  coupon,
}: DeleteCouponDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  const handleDelete = async () => {
    if (!coupon) return;

    setIsLoading(true);
    setError(null);

    try {
      await couponService.deleteCoupon(coupon._id);
      showSuccess(`Coupon "${coupon.code}" deleted successfully`, "Success");
      onSuccess();
      onClose();
    } catch (err: any) {
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Delete Failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !coupon) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Coupon"
      className="max-w-md"
    >
      <div className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600">
            <AlertTriangle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-100 rounded-full shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-gray-700">
              Are you sure you want to delete the coupon{" "}
              <span className="font-mono font-semibold bg-gray-100 px-2 py-0.5 rounded">
                {coupon.code}
              </span>
              ?
            </p>
            {coupon.usedCount > 0 && (
              <p className="text-sm text-amber-600 mt-2">
                ⚠️ This coupon has been used {coupon.usedCount} time(s).
                Deleting it will remove all usage history.
              </p>
            )}
            <p className="text-sm text-gray-500 mt-2">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? "Deleting..." : "Delete Coupon"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
